import React from "react";
import { Inbox, Radar, Eraser, Stethoscope, HardDriveDownload, Star, Send, Layers } from "lucide-react";

import RadialOrbitalTimeline from "../ui/radial-orbital-timeline";

const STEPS = [
  {
    id: 1,
    title: "Intake",
    date: "Step 1",
    icon: Inbox,
    status: "completed",
    energy: 100,
    relatedIds: [2],
    content: "Capture device identity, source, condition, and customer/job information.",
    includes: ["Serial capture", "Spec detection", "Asset tagging", "Batch assignment", "Chain-of-custody"],
  },
  {
    id: 2,
    title: "Preflight",
    date: "Step 2",
    icon: Radar,
    status: "in-progress",
    energy: 90,
    relatedIds: [1, 3],
    content:
      "Check whether a device is ready for erasure, assessment, reimaging, or resale — before wasting time on a workflow that will fail.",
    includes: ["MDM / lock risk", "Secure Boot & TPM", "BitLocker state", "Storage & battery", "Driver availability"],
  },
  {
    id: 3,
    title: "Erasure",
    date: "Step 3",
    icon: Eraser,
    status: "in-progress",
    energy: 80,
    relatedIds: [2, 4],
    content: "Run, manage, or record secure erasure workflows, with results attached to the asset record.",
    includes: ["Workflow tracking", "Erasure status", "Certificate support", "Failure reason codes", "Customer-ready evidence"],
  },
  {
    id: 4,
    title: "Assessment",
    date: "Step 4",
    icon: Stethoscope,
    status: "in-progress",
    energy: 70,
    relatedIds: [3, 5],
    content: "Assess device functionality, health, condition, and resale readiness.",
    includes: ["Hardware diagnostics", "Storage & battery", "Screen / keyboard / camera", "Network & ports", "Pass/fail status"],
  },
  {
    id: 5,
    title: "Reimage",
    date: "Step 5",
    icon: HardDriveDownload,
    status: "in-progress",
    energy: 60,
    relatedIds: [4, 6],
    content: "Prepare devices for resale or redeployment with repeatable reimaging workflows.",
    includes: ["Windows reimaging", "Local image support", "Driver capture & reuse", "Fingerprint matching", "Post-image verify"],
  },
  {
    id: 6,
    title: "Grade",
    date: "Step 6",
    icon: Star,
    status: "in-progress",
    energy: 50,
    relatedIds: [5, 7],
    content: "Record cosmetic and functional condition against consistent grading rules, with evidence.",
    includes: ["Cosmetic & functional grades", "Photo evidence", "Defect notes", "Condition checks", "Human review"],
  },
  {
    id: 7,
    title: "Resell / Redeploy",
    date: "Step 7",
    icon: Send,
    status: "pending",
    energy: 40,
    relatedIds: [6],
    content: "Route the device into the correct next step based on its verified condition.",
    includes: [
      "Resale-ready",
      "Redeployment-ready",
      "Repair required",
      "Battery/screen replace",
      "MDM quarantine",
      "Parts harvesting",
      "Recycling",
      "Manual review",
    ],
  },
];

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="relative bg-white font-body">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-brand-bg px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            How it works
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
            From Intake to <span className="text-brand-gradient">Resale-Ready</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink sm:text-lg">
            Every device follows a clear, evidence-backed workflow — so your team always knows what happened,
            what passed, what failed, and what should happen next.
          </p>
        </div>

        {/* callout band */}
        <div className="mt-8 flex items-start gap-3 rounded-2xl border border-brand-orchid/30 bg-brand-wash/60 p-5">
          <Layers className="mt-0.5 h-5 w-5 shrink-0 text-brand-magenta" />
          <p className="text-sm leading-relaxed text-brand-plum">
            Every device carries its own status and reason codes, so batches stay visible and failed jobs
            become clear next actions instead of dead ends.
          </p>
        </div>

        {/* orbital timeline */}
        <div className="mt-10">
          <RadialOrbitalTimeline timelineData={STEPS} />
          <p className="mt-3 text-center text-xs text-muted">
            Tap any step to see what it includes. The lifecycle auto-rotates.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
